// FileListPage.tsx
import React, { useState, useRef, useCallback, useEffect } from "react";
import filesUploadImage from "/upload_files.png";
import { apiService } from "@services/ApiService";
import { FileListProps, ServerFile } from "@shared/interfaces/ProjectsFiles";
import "handsontable/dist/handsontable.full.min.css";
import { toastService } from "@services/ToastService";
import { FileUploadProps } from "@shared/interfaces/ProjectsFiles";
import DocxPreview from "@pages/Project/components/Tabs/DocxPreview";
import PdfPreview from "@pages/Project/components/Tabs/PdfPreview";
import { statusToastService } from "@services/FileStatusService";
import FileStatusPopup from "@pages/Project/components/Tabs/components/Anomaly/FileStatusPopup";
import Pagination from "@shared/components/Pagination";
import { fileService } from "@services/FileService";
import StatusIcon from "@pages/Project/components/Tabs/components/Anomaly/StatusIcon";
import { Entry } from "@shared/interfaces/Anomaly";
import { useResumeAnomalyTasks } from "./components/Anomaly/useAnomalyWebSocket";
import ExcelPreviewModal from "./ExcelPreviewModal";
import { gdriveService } from "@services/GdriveService";
import { DriveFolder } from "@shared/interfaces/CloudFiles";
import ExternalFilePreview, {
  ExternalFileData,
} from "./components/Files/ExternalFilePreview";
import { LocalFilesFolders } from "./components/Files/LocalFilesFolders";
import LocalFilesFoldersSkeleton from "./components/Files/LocalFilesFoldersSkeleton";
import DriveFolderPreview from "./components/Files/DriveFolderPreview";
import useGDrive from "./components/Files/useGDrive";
import useOneDrive from "./components/Files/useOneDrive";
import useLocalFiles from "./components/Files/useLocalFiles";
import Loader from "@shared/components/Loader";

/** File icon by type */
export const getFileIcon = (fileType: string) => {
  switch (fileType.toLowerCase()) {
    case "doc":
    case "docx":
      return "/FilesIcon/docx.svg";
    case "xls":
    case "xlsx":
      return "/FilesIcon/xlsx.svg";
    case "zip":
      return "/FilesIcon/zip.svg";
    case "csv":
      return "/FilesIcon/csv.svg";
    case "jpeg":
    case "png":
    case "jpg":
      return "/FilesIcon/image.svg";
    case "pdf":
      return "/FilesIcon/pdf.svg";
    default:
      return "/FileIcon/genericfile.svg";
  }
};

export const normalizeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesUploaded,
  dragging,
}) => {
  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    onFilesUploaded(files);
  };

  return (
    <div
      className={`flex flex-col items-center justify-center bg-white rounded-lg p-8 border-dashed border-2 ${
        dragging ? "border-blue-600" : "border-gray-300"
      }`}
    >
      <img src={filesUploadImage} alt="Upload" className="w-32 mb-4" />
      <p className="text-lg font-bold text-gray-500 mb-2 p-6">
        Drag and drop your files here
      </p>
      <p className="text-sm text-gray-400">
        CSV, DOC, DOCX, PPT, XLS, XLSX, ZIP
      </p>
      <div className="mt-4 p-2">
        <input
          type="file"
          multiple
          className="hidden"
          id="fileInput"
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.png,.jpg"
          onChange={handleFileInputChange}
        />
        <label
          htmlFor="fileInput"
          className="text-blue-600 underline cursor-pointer"
        >
          Or choose files
        </label>
      </div>
    </div>
  );
};

export const FileListPage: React.FC<FileListProps> = ({
  checkedItems,
  setCheckedItems,
  setFolderView,
  folderView,
  projectId,
  handleFileUpload,
  dragging,
  // Local
  refreshOpenedFolder,
  setRefreshOpenedFolder,

  // GDrive
  refreshGDriveExtFiles,
  refreshGdriveFolders,

  // OneDrive
  refreshOneDriveExtFiles,
  refreshOnedriveFolders,

  isNewItemMenuOpen,
  setIsNewItemMenuOpen,
  isFolderCreating,
  setIsFolderCreating,
  newFolderName,
  setNewFolderName,
  isSaving,
  setIsSaving,
  // defaultNewFolderName,
}) => {
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null);
  const [isDocxModalOpen, setIsDocxModalOpen] = useState<boolean>(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const [isXlsModalOpen, setIsXlsModalOpen] = useState<boolean>(false);
  const [excelFileType, setExcelFileType] = useState<string>("");
  const [excelBlob, setExcelBlob] = useState<Blob | null>(null);

  const [docxBlob, setDocxBlob] = useState<Blob | null>(null);

  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupData, setPopupData] = useState<Entry>({} as Entry);
  const [searchLocal, setSearchLocal] = useState("");
  const [folderLoading, setFolderLoading] = useState(false);

  const [gDriveFoldersLoading, setGDriveFoldersLoading] = useState(true);
  const [oneDriveFoldersLoading, setOneDriveFoldersLoading] = useState(true);
  const [isLocalFilesVisible, setIsLocalFilesVisible] = useState(true);
  const [loadingExternalFiles, setLoadingExternalFiles] = useState(true);
  const [loadingExternalFilesOneDrive, setLoadingExternalFilesOneDrive] =
    useState(true);
  const [folders, setFolders] = useState<any>([]);
  const [oneDriveFolders, setOneDriveFolders] = useState<any>([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [folderTotalPages, setFolderTotalPages] = useState<number>(1);

  const [externalFilesData, setExternalFilesData] = useState<ExternalFileData>({
    last_sync: { google: "" },
    total_count: 0,
    files: [],
  });

  const [externalFilesDataOneDrive, setExternalFilesDataOneDrive] =
    useState<ExternalFileData>({
      last_sync: { google: "" },
      total_count: 0,
      files: [],
    });

  // File Loading and Preview
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const openExcelFile = async (fileId: string) => {
    setLoadingFileId(fileId);
    try {
      const response = await apiService.get(
        `/projects/${projectId}/files/${fileId}`,
        {
          responseType: "blob",
        }
      );

      const file = new Blob([response?.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      setExcelBlob(file);
      setIsXlsModalOpen(true);
    } catch (error) {
      console.error("Error loading XLSX file:", error);
    } finally {
      setLoadingFileId(null);
    }
  };

  const loadDocxFile = async (fileId: string) => {
    setLoadingFileId(fileId);
    try {
      const response = await apiService.get(
        `/projects/${projectId}/files/${fileId}`,
        {
          responseType: "blob",
        }
      );

      const fileBlob = new Blob([response?.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      setDocxBlob(fileBlob);
      setIsDocxModalOpen(true);
    } catch (error) {
      console.error("Error loading DOCX file:", error);
    } finally {
      setLoadingFileId(null);
    }
  };

  const loadPdfFile = async (fileId: string) => {
    setLoadingFileId(fileId);
    try {
      const response = await apiService.get(
        `/projects/${projectId}/files/${fileId}`,
        {
          responseType: "blob",
        }
      );

      const fileBlob = new Blob([response?.data], {
        type: "application/pdf",
      });

      setPdfBlob(fileBlob);
      setIsPdfModalOpen(true);
    } catch (error) {
      console.error("Error loading PDF file:", error);
    } finally {
      setLoadingFileId(null);
    }
  };

  const loadFile = async (
    fileId: string,
    fileType: string,
    fileSize: number,
    fileName: string
  ) => {
    setLoadingFileId(fileId);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            "File loading timed out, seems that file is corrupted or is too large to open"
          )
        );
      }, 15000);
    });

    const fileLoadingPromise = (async () => {
      if (fileSize < 50000000) {
        if (fileType === "docx") {
          await loadDocxFile(fileId);
        } else if (["xlsx", "csv"].includes(fileType)) {
          setExcelFileType(fileType);
          await openExcelFile(fileId);
        } else if (fileType === "pdf") {
          await loadPdfFile(fileId);
        } else {
          toastService.warn("File type not supported for opening");
        }
      } else {
        toastService.warn("File is too large to open");
      }
    })();

    try {
      await Promise.race([fileLoadingPromise, timeoutPromise]);
    } catch (error: any) {
      toastService.warn(error?.message || "Failed to open file");
    }
  };

  // Excel File Anamoly
  const clearStatusPopup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const fetchFileAnomaly = async (fileId: string) => {
    try {
      if (!projectId) return;
      const data = await fileService.getFileAnomalies(projectId, fileId);
      const anomalyData = {
        file_id: fileId,
        anomaly_data: {
          ...data,
        },
      };
      return anomalyData || null;
    } catch (error) {
      console.error(`Failed to fetch anomaly for file ${fileId}:`, error);
      return null;
    }
  };

  const setPopupDataAnomalyData = async (
    fileId: string,
    f: ServerFile = {} as ServerFile
  ) => {
    const data = await fetchFileAnomaly(fileId);
    let file;
    if (f) {
      file = f;
    } else {
      const viewFiles = (folderView?.files || []) as ServerFile[];
      file = viewFiles.find((f) => f.id === fileId) || ({} as ServerFile);
    }

    const popupData: Entry = data
      ? {
          ...data,
          file: file,
        }
      : {
          anomaly_data: { findings: [], total_count: 0 },
          file: {} as ServerFile,
        };

    setPopupData(popupData);

    return popupData;
  };

  const triggerStatusPopup = useCallback(
    (severity: string, anomaly_count: number) => {
      clearStatusPopup(); // Cancel any previous timeout

      timeoutRef.current = setTimeout(() => {
        if (severity === "Success") {
          statusToastService.success("No issues found.");
        } else if (anomaly_count === 0) {
          statusToastService.success("No issues found.");
        } else if (severity === "Low" || severity === "Medium") {
          statusToastService.warn(
            `${anomaly_count ?? ""} Potential ${
              anomaly_count > 1 ? "issues" : "issue"
            } detected`
          );
        } else {
          statusToastService.error(
            `${anomaly_count ?? ""} Potential ${
              anomaly_count > 1 ? "issues" : "issue"
            } detected`
          );
        }
      }, 500);
    },
    []
  );

  const handleShowPopup = async (
    fileId: string,
    file: ServerFile = {} as ServerFile
  ) => {
    const data = await setPopupDataAnomalyData(fileId, file);
    if (data?.file?.filename) setPopupVisible(true);
  };

  const handleClosePopup = () => {
    handleOpenFolder(folderView?.parent_id || 0);
    setTimeout(() => {
      setPopupVisible(false);
    }, 200);
  };

  // Fetch Google Drive folders and files for the project

  const {
    fetchFolders,
    fetchFolderFiles,
    loadExternalFiles,
    triggerGdriveFoldersRefresh,
  } = useGDrive({
    projectId,
    setGDriveFoldersLoading,
    setFolders,
    setExternalFilesData,
    setLoadingExternalFiles,
    gdriveService,
  });

  // Fetch One Drive folders andfiles for the project
  const activeTaskFileIds = useResumeAnomalyTasks(
    folderView?.files as ServerFile[]
  );

  const {
    fetchFoldersOneDrive,
    fetchOneDriveFolderFiles,
    loadExternalFilesOneDrive,
    triggerOnedriveFoldersRefresh,
  } = useOneDrive({
    projectId,
    setOneDriveFolders,
    setOneDriveFoldersLoading,
    setExternalFilesDataOneDrive,
    setLoadingExternalFilesOneDrive,
    setGDriveFoldersLoading,
  });

  const { handleOpenFolder, handleNavigateBreadcrumb, handleFolderCreated } =
    useLocalFiles({
      projectId,
      folderLoading,
      setFolderLoading,
      folderView,
      setFolderView,
      checkedItems,
      setCheckedItems,
      setCurrentPage,
      setFolderTotalPages,
      setRefreshOpenedFolder,
    });

  // General File Handling
  const selectRow = (fileUUID: string) => {
    if (!checkedItems.includes(fileUUID)) {
      setCheckedItems([...checkedItems, fileUUID]);
    }
  };

  const toggleChecked = (id: string) => {
    setCheckedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (!projectId) return;

    fetchFoldersOneDrive();
    loadExternalFilesOneDrive();

    fetchFolders();
    loadExternalFiles();
  }, [projectId]);

  useEffect(() => {
    setFolderView(null);
    if (projectId) {
      setCurrentPage?.(1);
      handleOpenFolder(0, 1);
    }
  }, [projectId]);

  useEffect(() => {
    if (refreshOpenedFolder > 0) {
      handleOpenFolder(folderView?.parent_id ?? 0, currentPage || 1);
    }
  }, [refreshOpenedFolder]);

  useEffect(() => {
    triggerGdriveFoldersRefresh();
  }, [refreshGdriveFolders]);

  useEffect(() => {
    triggerOnedriveFoldersRefresh();
  }, [refreshOnedriveFolders]);

  useEffect(() => {
    loadExternalFiles();
  }, [refreshGDriveExtFiles]);

  useEffect(() => {
    loadExternalFilesOneDrive();
  }, [refreshOneDriveExtFiles]);

  return (
    <>
      {(folderView &&
        (folderView.breadcrumbs.length > 0 ||
          folderView.files.length > 0 ||
          folderView.folders.length > 0)) ||
      isFolderCreating ||
      folders.length > 0 ||
      externalFilesData.files.length > 0 ||
      oneDriveFolders.length > 0 ||
      externalFilesDataOneDrive.files.length > 0 ? (
        <div className="bg-white pb-6 pt-2 flex flex-col">
          <div className=" flex-1 overflow-y-scroll scrollbar-hide ">
            {/* GDrive Folders */}
            {!gDriveFoldersLoading &&
              folders.map((folder: DriveFolder) => (
                <DriveFolderPreview
                  key={folder.id}
                  folder={folder}
                  projectId={projectId || ""}
                  fetchFolderFiles={fetchFolderFiles}
                  loadFile={loadFile}
                  setCheckedItems={setCheckedItems}
                  checkedItems={checkedItems}
                  loadingFileId={loadingFileId}
                  activeTaskFileIds={activeTaskFileIds}
                  handleShowPopup={handleShowPopup}
                  triggerStatusPopup={triggerStatusPopup}
                  clearStatusPopup={clearStatusPopup}
                  triggerFolderRefresh={triggerGdriveFoldersRefresh}
                  refreshFile={refreshGdriveFolders}
                  cloudType="gdrive"
                />
              ))}

            {/* GDrive External Files */}
            {!loadingExternalFiles && externalFilesData.files.length > 0 && (
              <ExternalFilePreview
                projectId={projectId || ""}
                loadFile={loadFile}
                loadFiles={loadExternalFiles}
                checkedItems={checkedItems}
                setCheckedItems={setCheckedItems}
                loadingFileId={loadingFileId}
                activeTaskFileIds={activeTaskFileIds}
                handleShowPopup={handleShowPopup}
                triggerStatusPopup={triggerStatusPopup}
                clearStatusPopup={clearStatusPopup}
                refreshFile={refreshGDriveExtFiles}
                data={externalFilesData}
                cloudType="gdrive"
              />
            )}

            {/* OneDrive Folders */}
            {!oneDriveFoldersLoading &&
              oneDriveFolders.map((folder: DriveFolder) => (
                <DriveFolderPreview
                  key={folder.id}
                  folder={folder}
                  projectId={projectId || ""}
                  fetchFolderFiles={fetchOneDriveFolderFiles}
                  loadFile={loadFile}
                  setCheckedItems={setCheckedItems}
                  checkedItems={checkedItems}
                  loadingFileId={loadingFileId}
                  activeTaskFileIds={activeTaskFileIds}
                  handleShowPopup={handleShowPopup}
                  triggerStatusPopup={triggerStatusPopup}
                  clearStatusPopup={clearStatusPopup}
                  triggerFolderRefresh={triggerOnedriveFoldersRefresh}
                  refreshFile={refreshGdriveFolders}
                  cloudType="onedrive"
                />
              ))}

            {/* OneDrive External Files */}
            {!loadingExternalFilesOneDrive &&
              externalFilesDataOneDrive.files.length > 0 && (
                <ExternalFilePreview
                  projectId={projectId || ""}
                  loadFile={loadFile}
                  loadFiles={loadExternalFilesOneDrive}
                  checkedItems={checkedItems}
                  setCheckedItems={setCheckedItems}
                  loadingFileId={loadingFileId}
                  activeTaskFileIds={activeTaskFileIds}
                  handleShowPopup={handleShowPopup}
                  triggerStatusPopup={triggerStatusPopup}
                  clearStatusPopup={clearStatusPopup}
                  refreshFile={refreshGDriveExtFiles}
                  data={externalFilesDataOneDrive}
                  cloudType="onedrive"
                />
              )}

            {/* Local Files */}
            {!folderLoading || isFolderCreating ? (
              <LocalFilesFolders
                key={`p:${projectId}-f:${
                  folderView ? folderView.parent_id ?? "root" : "root"
                }`}
                title="Local Documents"
                files={(folderView?.files as any) || []}
                folders={folderView?.folders || []}
                breadcrumbs={folderView?.breadcrumbs || []}
                projectId={projectId || ""}
                currentFolderId={folderView?.parent_id ?? null}
                onNavigateBreadcrumb={handleNavigateBreadcrumb}
                /** NEW: append immediately after successful creation */
                onFolderCreated={handleFolderCreated}
                // search control
                searchValue={searchLocal}
                onSearchChange={setSearchLocal}
                // visible control
                isVisible={isLocalFilesVisible}
                setIsLocalFilesVisible={setIsLocalFilesVisible}
                onToggleVisible={() => setIsLocalFilesVisible((v) => !v)}
                // selection control
                showCheckboxes={true}
                checkedItems={checkedItems}
                setCheckedItems={setCheckedItems}
                onToggleChecked={toggleChecked}
                onSelectRow={selectRow}
                //actions
                onOpenFolder={handleOpenFolder}
                onLoadFile={loadFile}
                // status popup
                onShowStatusPopup={(fileId, file) =>
                  handleShowPopup(fileId, file as any)
                }
                triggerStatusPopup={triggerStatusPopup}
                clearStatusPopup={clearStatusPopup}
                handleShowPopup={handleShowPopup}
                // utils
                normalizeDate={normalizeDate}
                getFileIcon={getFileIcon}
                // visuals
                loadingFileId={loadingFileId}
                activeTaskFileIds={activeTaskFileIds}
                // pagination
                currentPage={currentPage || 1}
                totalPages={folderTotalPages}
                onPageChange={(p) => {
                  setCurrentPage?.(p);
                  handleOpenFolder(folderView?.parent_id ?? 0, p);
                }}
                // external components
                StatusIcon={StatusIcon}
                Pagination={Pagination}
                //file upload
                onFilesUploaded={handleFileUpload}
                refreshOpenedFolder={refreshOpenedFolder}
                setRefreshOpenedFolder={setRefreshOpenedFolder}
                //
                isNewItemMenuOpen={isNewItemMenuOpen}
                setIsNewItemMenuOpen={setIsNewItemMenuOpen}
                isFolderCreating={isFolderCreating}
                setIsFolderCreating={setIsFolderCreating}
                newFolderName={newFolderName}
                setNewFolderName={setNewFolderName}
                // defaultNewFolderName={defaultNewFolderName}
                isSaving={isSaving}
                setIsSaving={setIsSaving}
              />
            ) : (
              <LocalFilesFoldersSkeleton />
            )}
          </div>

          {/* File Preview Modals */}
          {isDocxModalOpen && docxBlob && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
              onClick={() => setIsDocxModalOpen(false)}
            >
              <div
                className="bg-white w-3/4 h-[90%] rounded shadow-lg relative overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold">DOCX Preview</h2>
                  <button
                    onClick={() => setIsDocxModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="p-2 pt-0 h-full overflow-y-auto">
                  <DocxPreview file={docxBlob} />
                </div>
              </div>
            </div>
          )}

          {isXlsModalOpen && excelBlob && (
            <ExcelPreviewModal
              excelFileType={excelFileType}
              excelBlob={excelBlob}
              setIsXlsModalOpen={setIsXlsModalOpen}
              setExcelBlob={setExcelBlob}
            />
          )}

          {isPdfModalOpen && pdfBlob && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg shadow-lg w-3/4 h-[90%] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold">PDF Preview</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setIsPdfModalOpen(false);
                      setPdfBlob(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex-grow overflow-hidden rounded-lg">
                  <PdfPreview file={pdfBlob} />
                </div>
              </div>
            </div>
          )}

          {isPopupVisible && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
              <FileStatusPopup data={popupData} onClose={handleClosePopup} />
            </div>
          )}
        </div>
      ) : !folderLoading &&
        !gDriveFoldersLoading &&
        !loadingExternalFiles &&
        !oneDriveFoldersLoading &&
        !loadingExternalFilesOneDrive ? (
        <div className="flex items-center justify-center mt-24">
          <FileUpload onFilesUploaded={handleFileUpload} dragging={dragging} />
        </div>
      ) : (
        <div className="flex items-center justify-center mt-24">
          <Loader size="lg" color="#2162EB" />
        </div>
      )}
    </>
  );
};
